import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import {
	Add,
	ArrowForward,
	Autorenew,
	CheckCircle,
	Close,
	DeleteOutline,
	ErrorOutline,
	Groups,
	Payment,
	ReceiptLong,
	Remove,
	Redeem,
	StarBorder,
} from '@mui/icons-material';
import {
	EmbeddedCheckout,
	EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { apiV2 } from '../../axios';

import styles from './account.module.css';
import { DashboardToggle, PageLayout, PrimaryButton } from 'components';

const currencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	maximumFractionDigits: 0,
});

const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 0,
	maximumFractionDigits: 2,
});

const exactCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const statusLabels = {
	active: 'Active',
	free: 'Free',
	trialing: 'Trial period',
	past_due: 'Payment needed',
	canceled: 'Canceled',
	incomplete: 'Payment setup needed',
	incomplete_expired: 'Payment setup expired',
	unpaid: 'Payment overdue',
};

const activeSubscriptionStatuses = new Set([
	'active',
	'trialing',
	'past_due',
	'incomplete',
	'unpaid',
]);

const billingManagementSections = {
	paymentMethods: {
		title: 'Payment Methods',
		description:
			'Manage the payment methods your agency uses for automatic billing.',
	},
	invoices: {
		title: 'Invoices',
		description: 'Recent invoice history and payment status for your agency.',
	},
};

const isPaymentMethodExpired = (paymentMethod) => {
	const expMonth = Number(paymentMethod?.expMonth || 0);
	const expYear = Number(paymentMethod?.expYear || 0);
	if (!expMonth || !expYear) return false;
	const today = new Date();
	const currentMonth = today.getMonth() + 1;
	const currentYear = today.getFullYear();
	return expYear < currentYear || (expYear === currentYear && expMonth < currentMonth);
};

const isDraftInvoice = (invoice) =>
	String(invoice?.status || '').toLowerCase() === 'draft';

const paymentMethodExpirationValue = (paymentMethod) => {
	const expMonth = Number(paymentMethod?.expMonth || 0);
	const expYear = Number(paymentMethod?.expYear || 0);
	if (!expMonth || !expYear) return Number.POSITIVE_INFINITY;
	return expYear * 100 + expMonth;
};

const EmbeddedBillingCheckout = ({ clientSecret, stripePromise, onComplete }) => {
	const options = useMemo(
		() => ({
			clientSecret,
			onComplete,
		}),
		[clientSecret, onComplete]
	);
	return (
		<EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
			<EmbeddedCheckout className={styles.billingEmbeddedCheckout} />
		</EmbeddedCheckoutProvider>
	);
};

const Billing = () => {
	const { pathname } = useLocation();
	const isMd = useSelector((state) => state.mediaQuery.isMd);
	const [plans, setPlans] = useState([]);
	const [billing, setBilling] = useState(null);
	const [selectedPlanCode, setSelectedPlanCode] = useState('');
	const [paidSeats, setPaidSeats] = useState(1);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [sectionLoading, setSectionLoading] = useState(false);
	const [error, setError] = useState('');
	const [sectionError, setSectionError] = useState('');
	const [notice, setNotice] = useState('');
	const [paymentMethods, setPaymentMethods] = useState([]);
	const [paymentSetup, setPaymentSetup] = useState(null);
	const [subscriptionCheckout, setSubscriptionCheckout] = useState(null);
	const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);
	const [pendingSubscriptionChange, setPendingSubscriptionChange] =
		useState(null);
	const [subscriptionActionError, setSubscriptionActionError] = useState('');
	const [paymentActionLoading, setPaymentActionLoading] = useState('');
	const [subscriptionCheckoutCompleting, setSubscriptionCheckoutCompleting] =
		useState(false);
	const [editingSubscription, setEditingSubscription] = useState(false);
	const [invoices, setInvoices] = useState([]);

	const selectedPlan = useMemo(
		() => plans.find((plan) => plan.planCode === selectedPlanCode),
		[plans, selectedPlanCode]
	);

	const billingSection = useMemo(() => {
		if (pathname.includes('/billing/payment-methods')) return 'paymentMethods';
		if (pathname.includes('/billing/invoices')) return 'invoices';
		return 'plans';
	}, [pathname]);

	const managementSection = billingManagementSections[billingSection];

	const paymentStripePromise = useMemo(() => {
		if (!paymentSetup?.publishableKey) return null;
		return loadStripe(paymentSetup.publishableKey);
	}, [paymentSetup?.publishableKey]);

	const subscriptionStripePromise = useMemo(() => {
		if (!subscriptionCheckout?.publishableKey) return null;
		return loadStripe(subscriptionCheckout.publishableKey);
	}, [subscriptionCheckout?.publishableKey]);

	const closePaymentMethodSetup = () => {
		if (paymentActionLoading === 'confirm') return;
		setPaymentSetup(null);
	};

	const closeSubscriptionCheckout = () => {
		if (subscriptionCheckoutCompleting) return;
		setSubscriptionCheckout(null);
	};

	const deletingPaymentMethod =
		Boolean(paymentMethodToDelete?.id) &&
		paymentActionLoading === `delete:${paymentMethodToDelete.id}`;

	const closeRemovePaymentMethodDialog = () => {
		if (deletingPaymentMethod) return;
		setPaymentMethodToDelete(null);
	};

	const closeSubscriptionChangeDialog = () => {
		if (actionLoading) return;
		setPendingSubscriptionChange(null);
		setSubscriptionActionError('');
	};

	const hasSubscription =
		billing?.stripeSubscriptionId &&
		activeSubscriptionStatuses.has(billing?.status);

	const isCurrentSelection =
		billing?.planCode === selectedPlanCode &&
		Number(billing?.purchasedSeatQuantity || 0) === Number(paidSeats);

	const includedFreeSeatQuantity = Number(
		billing?.includedFreeSeatQuantity || 0
	);
	const giftedSeatQuantity = Number(billing?.giftedSeatQuantity || 0);
	const usedSeatQuantity = Number(billing?.usedSeatQuantity || 0);
	const purchasedSeatQuantity = Number(billing?.purchasedSeatQuantity || 0);
	const currentPlan = useMemo(
		() => plans.find((plan) => plan.planCode === billing?.planCode),
		[plans, billing?.planCode]
	);


	const monthlyAmountCents = (plan, seats) => {
		if (!plan) return 0;
		const normalizedSeats = Number(seats || 0);
		if (plan.pricingModel === 'per_unit') {
			return normalizedSeats * plan.overageUnitAmountCents;
		}
		const overageSeats = Math.max(
			0,
			normalizedSeats - plan.includedPaidSeats
		);
		return (
			plan.baseAmountCents +
			overageSeats * plan.overageUnitAmountCents
		);
	};

	const requiredPaidSeatsForPlan = (plan, billingData = billing) => {
		if (!plan) return 1;
		const includedSeats = Number(billingData?.includedFreeSeatQuantity || 0);
		const giftedSeats = Number(billingData?.giftedSeatQuantity || 0);
		const usedSeats = Number(billingData?.usedSeatQuantity || 0);
		return Math.max(
			plan.minQuantity,
			usedSeats - includedSeats - giftedSeats,
			0
		);
	};

	const selectedMinimumPaidSeats = requiredPaidSeatsForPlan(selectedPlan);
	const estimatedMonthlyCents = monthlyAmountCents(selectedPlan, paidSeats);
	const currentMonthlyCents = monthlyAmountCents(
		currentPlan,
		purchasedSeatQuantity
	);
	const previewCapacity =
		(selectedPlan?.includedFreeSeats || includedFreeSeatQuantity) +
		Number(paidSeats || 0) +
		giftedSeatQuantity;

	useEffect(() => {
		if (billingSection === 'plans') {
			loadBillingOptions();
		} else {
			setLoading(false);
			setError('');
			setNotice('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [billingSection]);

	useEffect(() => {
		if (billingSection === 'paymentMethods') {
			loadPaymentMethods();
		} else if (billingSection === 'invoices') {
			loadInvoices();
		} else {
			setSectionError('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [billingSection]);

	const returnUrl = () => {
		const configuredUrl = process.env.REACT_APP_STRIPE_REDIRECT;
		return configuredUrl || `${window.location.origin}/billing`;
	};

	const planDefaultQuantity = (plan, currentBilling) => {
		if (!plan) return 1;
		const requiredSeats = requiredPaidSeatsForPlan(plan, currentBilling);
		if (currentBilling?.planCode === plan.planCode) {
			return Math.max(
				requiredSeats,
				Number(currentBilling?.purchasedSeatQuantity || 0)
			);
		}
		return requiredSeats;
	};

	const applyBillingData = (planData, billingData) => {
		const firstPlan = planData[0];
		const currentPlan =
			planData.find((plan) => plan.planCode === billingData?.planCode) ||
			firstPlan;
		setPlans(planData);
		setBilling(billingData);
		setSelectedPlanCode(currentPlan?.planCode || '');
		setPaidSeats(planDefaultQuantity(currentPlan, billingData));
		setEditingSubscription(false);
	};

	const resetBillingDraft = () => {
		const currentPlan =
			plans.find((plan) => plan.planCode === billing?.planCode) || plans[0];
		setSelectedPlanCode(currentPlan?.planCode || '');
		setPaidSeats(planDefaultQuantity(currentPlan, billing));
		setEditingSubscription(false);
		setNotice('');
		setError('');
	};

	const startSubscriptionEdit = () => {
		const currentPlan =
			plans.find((plan) => plan.planCode === billing?.planCode) || plans[0];
		setSelectedPlanCode(currentPlan?.planCode || '');
		setPaidSeats(planDefaultQuantity(currentPlan, billing));
		setEditingSubscription(true);
		setNotice('');
		setError('');
	};

	const loadBillingOptions = async () => {
		setLoading(true);
		setError('');
		try {
			const response = await apiV2.get('/billing/options');
			applyBillingData(response.data?.plans || [], response.data?.billing || {});
		} catch (err) {
			setError(
				err?.response?.data?.message ||
					'Could not load billing options. Please refresh and try again.'
			);
		} finally {
			setLoading(false);
		}
	};

	const loadPaymentMethods = async () => {
		setSectionLoading(true);
		setSectionError('');
		try {
			const response = await apiV2.get('/billing/payment-methods');
			setPaymentMethods(response.data?.paymentMethods || []);
		} catch (err) {
			setSectionError(
				err?.response?.data?.message ||
					'Could not load payment methods. Please refresh and try again.'
			);
		} finally {
			setSectionLoading(false);
		}
	};

	const applyPaymentMethodResponse = (data) => {
		setPaymentMethods(data?.paymentMethods || []);
	};

	const loadInvoices = async () => {
		setSectionLoading(true);
		setSectionError('');
		try {
			const response = await apiV2.get('/billing/invoices');
			setInvoices(
				(response.data?.invoices || []).filter(
					(invoice) => !isDraftInvoice(invoice)
				)
			);
		} catch (err) {
			setSectionError(
				err?.response?.data?.message ||
					'Could not load invoices. Please refresh and try again.'
			);
		} finally {
			setSectionLoading(false);
		}
	};

	const selectPlan = (plan) => {
		if (!editingSubscription) return;
		setSelectedPlanCode(plan.planCode);
		setPaidSeats(planDefaultQuantity(plan, billing));
		setNotice('');
		setError('');
	};

	const updatePaidSeats = (nextSeats) => {
		if (!editingSubscription) return;
		const normalizedSeats = Number(nextSeats);
		if (Number.isNaN(normalizedSeats)) {
			setPaidSeats(selectedMinimumPaidSeats);
			return;
		}
		setPaidSeats(Math.max(selectedMinimumPaidSeats, normalizedSeats));
		setNotice('');
		setError('');
	};

	const completeSubscriptionCheckout = useCallback(async () => {
		setSubscriptionCheckoutCompleting(true);
		setError('');
		try {
			setSubscriptionCheckout(null);
			await loadBillingOptions();
			setNotice('Payment complete. Your plan is being finalized.');
		} catch (err) {
			setError(
				err?.response?.data?.message ||
					'Payment completed, but billing could not be refreshed.'
			);
		} finally {
			setSubscriptionCheckoutCompleting(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePrimaryAction = async () => {
		if (!selectedPlan || isCurrentSelection || actionLoading) return;
		setActionLoading(true);
		setSubscriptionActionError('');
		setError('');
		setNotice('');
		const payload = {
			planCode: selectedPlan.planCode,
			purchasedSeatQuantity: paidSeats,
		};

		try {
			if (hasSubscription) {
				const response = await apiV2.post('/billing/subscription', payload);
				setBilling(response.data?.billing || billing);
				setNotice(
					response.data?.changeTiming === 'period_end'
						? 'The plan change is scheduled for your next renewal.'
						: 'Your plan change was applied.'
				);
				await loadBillingOptions();
				setPendingSubscriptionChange(null);
			} else {
				const response = await apiV2.post('/billing/checkout', {
					...payload,
					embedded: true,
					returnUrl: returnUrl(),
				});
				if (!response.data?.clientSecret || !response.data?.publishableKey) {
					throw new Error('Payment checkout could not be started.');
				}
				setSubscriptionCheckout({
					...response.data,
					planName: selectedPlan.name,
					purchasedSeatQuantity: paidSeats,
					estimatedMonthlyCents,
				});
				setPendingSubscriptionChange(null);
			}
		} catch (err) {
			const message =
				err?.response?.data?.message ||
				err?.message ||
				'Could not update billing. Please try again.';
			setError(message);
			setSubscriptionActionError(message);
		} finally {
			setActionLoading(false);
		}
	};

	const startPaymentMethodSetup = async ({
		mode = 'add',
		paymentMethod = null,
	} = {}) => {
		setPaymentActionLoading('setup');
		setSectionError('');
		setNotice('');
		try {
			const response = await apiV2.post('/billing/payment-methods/setup-session', {
				returnUrl: `${window.location.origin}/billing/payment-methods`,
			});
			if (!response.data?.clientSecret || !response.data?.publishableKey) {
				throw new Error('Payment setup could not be started.');
			}
			setPaymentSetup({
				...response.data,
				mode,
				replacesPaymentMethod: paymentMethod,
			});
		} catch (err) {
			setSectionError(
				err?.response?.data?.message ||
					err?.message ||
					'Could not start payment setup. Please try again.'
			);
		} finally {
			setPaymentActionLoading('');
		}
	};

	const completePaymentMethodSetup = useCallback(async () => {
		setPaymentActionLoading('confirm');
		setSectionError('');
		try {
			const response = await apiV2.post('/billing/payment-methods/default', {
				checkoutSessionId: paymentSetup?.checkoutSessionId,
			});
			const replacingPaymentMethod = paymentSetup?.mode === 'replace';
			const replacedPaymentMethodId = paymentSetup?.replacesPaymentMethod?.id;
			if (replacingPaymentMethod && replacedPaymentMethodId) {
				try {
					const deleteResponse = await apiV2.delete(
						`/billing/payment-methods/${replacedPaymentMethodId}`
					);
					applyPaymentMethodResponse(deleteResponse.data);
					setNotice(
						'Replacement payment method saved and expired payment method removed.'
					);
				} catch (deleteErr) {
					applyPaymentMethodResponse(response.data);
					setNotice(
						deleteErr?.response?.data?.message ||
							'Replacement payment method saved, but the expired payment method could not be removed.'
					);
				}
			} else {
				applyPaymentMethodResponse(response.data);
				setNotice('Payment method saved and set as default.');
			}
			setPaymentSetup(null);
		} catch (err) {
			setSectionError(
				err?.response?.data?.message ||
					'Payment method was saved, but the default could not be updated.'
			);
			await loadPaymentMethods();
		} finally {
			setPaymentActionLoading('');
		}
	}, [
		paymentSetup?.checkoutSessionId,
		paymentSetup?.mode,
		paymentSetup?.replacesPaymentMethod?.id,
	]);

	const setDefaultSavedPaymentMethod = async (paymentMethodId) => {
		setPaymentActionLoading(`default:${paymentMethodId}`);
		setSectionError('');
		setNotice('');
		try {
			const response = await apiV2.post('/billing/payment-methods/default', {
				paymentMethodId,
			});
			applyPaymentMethodResponse(response.data);
			setNotice('Default payment method updated.');
		} catch (err) {
			setSectionError(
				err?.response?.data?.message ||
					'Could not update the default payment method.'
			);
		} finally {
			setPaymentActionLoading('');
		}
	};

	const requestDeleteSavedPaymentMethod = (paymentMethod) => {
		if (paymentMethods.length <= 1) return;
		setPaymentMethodToDelete(paymentMethod);
	};

	const deleteSavedPaymentMethod = async () => {
		const paymentMethodId = paymentMethodToDelete?.id;
		if (!paymentMethodId || paymentMethods.length <= 1) return;
		setPaymentActionLoading(`delete:${paymentMethodId}`);
		setSectionError('');
		setNotice('');
		try {
			const response = await apiV2.delete(
				`/billing/payment-methods/${paymentMethodId}`
			);
			applyPaymentMethodResponse(response.data);
			setPaymentMethodToDelete(null);
			setNotice('Payment method removed.');
		} catch (err) {
			setSectionError(
				err?.response?.data?.message || 'Could not remove payment method.'
			);
		} finally {
			setPaymentActionLoading('');
		}
	};

	const formatMoney = (amountCents) =>
		currencyFormatter.format(Number(amountCents || 0) / 100);

	const formatUnitMoney = (amountCents) =>
		compactCurrencyFormatter.format(Number(amountCents || 0) / 100);

	const formatExactMoney = (amountCents) =>
		exactCurrencyFormatter.format(Number(amountCents || 0) / 100);

	const formatDate = (seconds) => {
		if (!seconds) return 'N/A';
		return new Intl.DateTimeFormat('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		}).format(new Date(seconds * 1000));
	};

	const formatInvoiceMoney = (amountCents, currency = 'USD') => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: (currency || 'USD').toUpperCase(),
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		}).format(Number(amountCents || 0) / 100);
	};

	const formatPaymentMethodLabel = (paymentMethod) => {
		if (!paymentMethod) return 'Payment method';
		const methodType = String(paymentMethod.type || '').toLowerCase();
		const fallbackLabel =
			methodType === 'us_bank_account'
				? 'Bank account'
				: methodType
				? methodType
						.split('_')
						.map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
						.join(' ')
				: 'Payment method';
		const brand = paymentMethod.brand || paymentMethod.bankName || fallbackLabel;
		return `${brand.charAt(0).toUpperCase()}${brand.slice(1)} ${
			paymentMethod.last4 ? `ending in ${paymentMethod.last4}` : ''
		}`.trim();
	};

	const formatPaymentMethodExpiration = (paymentMethod) => {
		if (!paymentMethod?.expMonth || !paymentMethod?.expYear) {
			if (paymentMethod?.type && paymentMethod.type !== 'card') {
				return 'No expiration';
			}
			return 'Expiration not available';
		}
		return `${isPaymentMethodExpired(paymentMethod) ? 'Expired' : 'Expires'} ${
			paymentMethod.expMonth
		}/${paymentMethod.expYear}`;
	};

	const renderPaymentMethodLoading = () => (
		<div
			className={styles.billingPaymentMethodGrid}
			aria-label='Loading payment methods'>
			{[0, 1].map((index) => (
				<div
					aria-hidden='true'
					className={`${styles.billingPaymentMethodCard} ${styles.billingPaymentMethodSkeletonCard}`}
					key={index}>
					<div className={styles.billingPaymentMethodSkeletonHeader}>
						<span />
						<div>
							<span />
							<span />
						</div>
					</div>
					<div className={styles.billingPaymentMethodSkeletonActions}>
						<span />
						<span />
					</div>
				</div>
			))}
		</div>
	);

	const paymentSetupIsReplace = paymentSetup?.mode === 'replace';
	const paymentSetupTitle = paymentSetupIsReplace
		? 'Replace payment method'
		: 'Add payment method';
	const paymentSetupDescription = paymentSetupIsReplace
		? `Add a new payment method for ${formatPaymentMethodLabel(
				paymentSetup?.replacesPaymentMethod
		  )}. The new payment method will become the default payment method.`
		: 'This payment method will be saved for future agency billing and set as the default payment method.';
	const subscriptionCheckoutTitle = 'Review payment';
	const subscriptionCheckoutDescription = subscriptionCheckout
		? `${subscriptionCheckout.planName} with ${
				subscriptionCheckout.purchasedSeatQuantity
		  } paid seats. ${formatMoney(
				subscriptionCheckout.estimatedMonthlyCents
		  )} per month.`
		: 'Complete checkout to start this plan.';

	const invoiceStatusLabel = (status) => {
		const labels = {
			draft: 'Preparing',
			open: 'Open',
			paid: 'Paid',
			uncollectible: 'Past due',
			void: 'Void',
		};
		return labels[status] || status || 'Unknown';
	};

	const invoiceHasBalanceDue = (invoice) =>
		Number(invoice?.amountRemaining || 0) > 0;

	const invoiceDocumentUrl = (invoice) =>
		invoice?.invoicePdf || invoice?.hostedInvoiceUrl;

	const invoiceBalanceByCurrency = invoices.reduce((totals, invoice) => {
		const amountRemaining = Number(invoice?.amountRemaining || 0);
		if (amountRemaining <= 0) return totals;
		const currency = (invoice?.currency || 'USD').toUpperCase();
		return {
			...totals,
			[currency]: (totals[currency] || 0) + amountRemaining,
		};
	}, {});

	const invoiceOpenBalance = Object.entries(invoiceBalanceByCurrency)
		.map(([currency, amountCents]) =>
			formatInvoiceMoney(amountCents, currency)
		)
		.join(' + ');

	const invoiceOpenCount = invoices.filter(invoiceHasBalanceDue).length;
	const latestInvoice = invoices[0] || null;
	const invoiceTableHeaders = [
		'Invoice',
		'Period',
		'Total',
		'Balance',
		'Status',
		'Document',
	];

	const renderInvoiceLoading = () => (
		<div className={styles.billingInvoiceTable} aria-label='Loading invoices'>
			<div className={styles.billingInvoiceTableHeader} aria-hidden='true'>
				{invoiceTableHeaders.map((header) => (
					<span key={header}>{header}</span>
				))}
			</div>
			<div className={styles.billingInvoiceList}>
				{[0, 1, 2].map((index) => (
					<div
						aria-hidden='true'
						className={`${styles.billingInvoiceRow} ${styles.billingInvoiceSkeletonRow}`}
						key={index}>
						<span className={styles.billingInvoiceSkeletonBlock} />
						<span className={styles.billingInvoiceSkeletonText} />
						<span className={styles.billingInvoiceSkeletonText} />
						<span className={styles.billingInvoiceSkeletonText} />
						<span className={styles.billingInvoiceSkeletonPill} />
						<span className={styles.billingInvoiceSkeletonLink} />
					</div>
				))}
			</div>
		</div>
	);

	const renderPlansLoading = () => (
		<div
			className={styles.billingPlanLoading}
			aria-label='Loading plans and seats'>
			<section
				className={`${styles.billingSummaryBand} ${styles.billingPlanSkeletonSummary}`}
				aria-hidden='true'>
				<div className={styles.billingHeroTop}>
					<div>
						<span className={styles.billingPlanSkeletonEyebrow} />
						<span className={styles.billingPlanSkeletonTitle} />
						<span className={styles.billingPlanSkeletonStatus} />
					</div>
					<div className={styles.billingSummaryMeta}>
						{[0, 1].map((index) => (
							<div className={styles.billingPlanSkeletonMeta} key={index}>
								<span />
								<strong />
							</div>
						))}
					</div>
				</div>
			</section>

			<section
				className={`${styles.billingMetricGrid} ${styles.billingPlanSkeletonMetricGrid}`}
				aria-hidden='true'>
				{[0, 1, 2, 3].map((index) => (
					<div
						className={`${styles.billingMetric} ${styles.billingPlanSkeletonMetric}`}
						key={index}>
						<span className={styles.billingPlanSkeletonIcon} />
						<span className={styles.billingPlanSkeletonLabel} />
						<strong />
						<small />
					</div>
				))}
			</section>

			<section
				className={`${styles.billingSectionHeader} ${styles.billingPlanSkeletonSectionHeader}`}
				aria-hidden='true'>
				<div>
					<span className={styles.billingPlanSkeletonHeading} />
					<span className={styles.billingPlanSkeletonCopy} />
				</div>
				<span className={styles.billingPlanSkeletonButton} />
			</section>
		</div>
	);

	const planPricingDetail = (plan) => {
		if (plan.pricingModel === 'per_unit') {
			return `Pay as you go at ${formatUnitMoney(
				plan.overageUnitAmountCents
			)} per paid seat.`;
		}
		return `Includes ${plan.includedPaidSeats} paid seats; extra seats are ${formatUnitMoney(
			plan.overageUnitAmountCents
		)} each.`;
	};

	const planEstimateContext = (plan, estimateSeats) => {
		const requiredSeats = requiredPaidSeatsForPlan(plan);
		if (Number(paidSeats || 0) < requiredSeats) {
			return `Starts at ${estimateSeats} paid seats`;
		}
		return `For ${estimateSeats} paid seats`;
	};

	const planEstimateForDraft = (plan) => {
		const estimateSeats = Math.max(
			Number(paidSeats || 0),
			requiredPaidSeatsForPlan(plan)
		);
		return {
			amountCents: monthlyAmountCents(plan, estimateSeats),
			seats: estimateSeats,
		};
	};

	const savingsPercentForPlan = (plan) => {
		if (!selectedPlan || selectedPlan.planCode === plan.planCode) return null;
		const candidateMinimumSeats = requiredPaidSeatsForPlan(plan);
		if (Number(paidSeats || 0) < candidateMinimumSeats) return null;
		const selectedCost = monthlyAmountCents(selectedPlan, paidSeats);
		const candidateCost = monthlyAmountCents(plan, paidSeats);
		if (!selectedCost || candidateCost >= selectedCost) return null;
		const savingsPercent = Math.round(
			((selectedCost - candidateCost) / selectedCost) * 100
		);
		return savingsPercent > 0 ? savingsPercent : null;
	};

	const changeDirectionLabel = (plan) => {
		if (!currentPlan || plan.planCode === currentPlan.planCode) return null;
		const currentIndex = plans.findIndex(
			(candidate) => candidate.planCode === currentPlan.planCode
		);
		const planIndex = plans.findIndex(
			(candidate) => candidate.planCode === plan.planCode
		);
		if (currentIndex === -1 || planIndex === -1) return null;
		return planIndex > currentIndex ? 'Upgrade' : 'Downgrade';
	};

	const isImmediateSubscriptionChange = (plan, seats) => {
		if (!hasSubscription || !plan) return false;
		if (!currentPlan) return true;
		return (
			plan.rank > currentPlan.rank ||
			Number(seats || 0) > purchasedSeatQuantity
		);
	};

	const subscriptionChangeTiming = () => {
		if (!hasSubscription) return 'checkout';
		return isImmediateSubscriptionChange(selectedPlan, paidSeats)
			? 'immediate'
			: 'period_end';
	};

	const subscriptionChangeDetails = () => {
		if (!selectedPlan) return null;
		const timing = subscriptionChangeTiming();
		const pendingChange = billing?.pendingChange;
		const targetSeats = Number(paidSeats || 0);
		const currentPlanName = billing?.planName || currentPlan?.name || 'Free';
		const currentSeats = Number(purchasedSeatQuantity || 0);
		const renewalDate = billing?.currentPeriodEnd
			? formatDate(billing.currentPeriodEnd)
			: null;
		const targetPlanLine = `${selectedPlan.name} with ${targetSeats} paid seat${
			targetSeats === 1 ? '' : 's'
		}`;
		const currentPlanLine = `${currentPlanName} with ${currentSeats} paid seat${
			currentSeats === 1 ? '' : 's'
		}`;
		const notes = [];

		if (pendingChange) {
			notes.push(
				`This replaces the scheduled change to ${pendingChange.planName} with ${pendingChange.purchasedSeatQuantity} paid seats.`
			);
		}

		if (selectedPlanUsesRaisedMinimum) {
			notes.push(
				`${selectedPlan.name} requires at least ${selectedMinimumPaidSeats} paid seats for your current users, so this change uses ${targetSeats}.`
			);
		}

		if (timing === 'checkout') {
			return {
				timing,
				title: 'Confirm plan',
				description:
					'You will review payment in this portal before the subscription starts.',
				confirmLabel: 'Continue to payment',
				timingLabel: 'Starts after payment',
				current: 'Free plan',
				currentMonthlyCost: formatMoney(0),
				target: targetPlanLine,
				targetMonthlyCost: formatMoney(estimatedMonthlyCents),
				notes: [
					...notes,
					'No payment is collected until checkout is completed.',
				],
			};
		}

		if (timing === 'immediate') {
			return {
				timing,
				title: 'Apply billing change now?',
				description:
					'This change applies immediately. Any prorated difference will be billed now.',
				confirmLabel: 'Apply change now',
				timingLabel: 'Applies now',
				current: currentPlanLine,
				currentMonthlyCost: formatMoney(currentMonthlyCents),
				target: targetPlanLine,
				targetMonthlyCost: formatMoney(estimatedMonthlyCents),
				notes,
			};
		}

		return {
			timing,
			title: 'Schedule billing change?',
			description: renewalDate
				? `This change takes effect at your next renewal on ${renewalDate}. Your current plan stays active until then.`
				: 'This change takes effect at your next renewal. Your current plan stays active until then.',
			confirmLabel: 'Schedule change',
			timingLabel: renewalDate ? `Starts ${renewalDate}` : 'Starts next renewal',
			current: currentPlanLine,
			currentMonthlyCost: formatMoney(currentMonthlyCents),
			target: targetPlanLine,
			targetMonthlyCost: formatMoney(estimatedMonthlyCents),
			notes,
		};
	};

	const prorationDetailsFromPreview = (preview) => {
		const totalCents = Number(preview?.totalCents ?? preview?.amountDueCents);
		const amountDueCents = Number(preview?.amountDueCents ?? totalCents);
		if (Number.isNaN(totalCents) || Number.isNaN(amountDueCents)) {
			return {
				loading: false,
				unavailable: true,
				label: 'Proration',
				amount: 'Unavailable',
				detail:
					'The final amount will be reflected after the change is applied.',
			};
		}

		if (totalCents < 0) {
			return {
				loading: false,
				label: 'Prorated credit',
				amount: formatExactMoney(Math.abs(totalCents)),
				detail: 'This credit is applied to your billing balance.',
			};
		}

		return {
			loading: false,
			label: 'Due now',
			amount: formatExactMoney(Math.max(0, amountDueCents)),
			detail: 'Includes the prorated difference for the current period.',
		};
	};

	const subscriptionProcessingCopy = () => {
		if (!pendingSubscriptionChange) return null;
		if (pendingSubscriptionChange.timing === 'checkout') {
			return {
				title: 'Preparing payment',
				detail: 'This usually takes a few seconds.',
			};
		}
		if (pendingSubscriptionChange.timing === 'period_end') {
			return {
				title: 'Scheduling your change',
				detail: 'Your current plan stays active while this is processed.',
			};
		}
		return {
			title: 'Applying your change',
			detail: 'Please keep this window open while billing is updated.',
		};
	};

	const openSubscriptionChangeConfirmation = async () => {
		if (!selectedPlan || isCurrentSelection) return;
		setSubscriptionActionError('');
		const details = subscriptionChangeDetails();
		if (!details) return;
		const requestId = `${selectedPlan.planCode}:${paidSeats}:${Date.now()}`;
		const shouldPreviewProration = details.timing === 'immediate';
		setPendingSubscriptionChange({
			...details,
			requestId,
			proration: shouldPreviewProration
				? {
						loading: true,
						label: 'Due now',
						amount: 'Checking...',
						detail: 'Checking the prorated difference for this change.',
				  }
				: null,
		});

		if (!shouldPreviewProration) return;

		try {
			const response = await apiV2.post('/billing/subscription/preview', {
				planCode: selectedPlan.planCode,
				purchasedSeatQuantity: paidSeats,
			});
			const proration = prorationDetailsFromPreview(response.data);
			setPendingSubscriptionChange((current) =>
				current?.requestId === requestId
					? { ...current, proration }
					: current
			);
		} catch {
			setPendingSubscriptionChange((current) =>
				current?.requestId === requestId
					? {
							...current,
							proration: {
								loading: false,
								unavailable: true,
								label: 'Proration',
								amount: 'Unavailable',
								detail:
									'The final amount will be reflected after the change is applied.',
							},
					  }
					: current
			);
		}
	};

	const primaryActionLabel = () => {
		if (isCurrentSelection) return 'Current selection';
		if (hasSubscription) return 'Save plan change';
		return 'Continue to payment';
	};

	const subscriptionActionLabel = hasSubscription
		? 'Change subscription'
		: 'Change Plan';
	const selectedPlanUsesRaisedMinimum =
		Boolean(selectedPlan) &&
		selectedMinimumPaidSeats > Number(selectedPlan?.minQuantity || 0);

	const metricItems = [
		{
			label: 'Included seats',
			value: billing?.includedFreeSeatQuantity ?? 0,
			detail: 'Included with every agency',
			icon: CheckCircle,
		},
		...(giftedSeatQuantity > 0
			? [
					{
						label: 'Gifted seats',
						value: giftedSeatQuantity,
						detail: 'Added by Ella',
						icon: Redeem,
					},
			  ]
			: []),
		{
			label: 'Purchased seats',
			value: billing?.purchasedSeatQuantity ?? 0,
			detail: 'Seats in your plan',
			icon: Payment,
		},
		{
			label: 'Usage',
			value: `${billing?.usedSeatQuantity ?? 0} / ${
				billing?.totalSeatQuantity ?? 0
			}`,
			detail: `${billing?.remainingSeatQuantity ?? 0} available`,
			icon: Groups,
		},
	];
	const metricGridStyle = {
		'--billing-metric-columns': Math.max(metricItems.length, 1),
	};

	const renderPaymentMethods = () => {
		const sortedPaymentMethods = [...paymentMethods].sort(
			(firstPaymentMethod, secondPaymentMethod) => {
				if (firstPaymentMethod.isDefault) return -1;
				if (secondPaymentMethod.isDefault) return 1;
				const firstExpired = isPaymentMethodExpired(firstPaymentMethod);
				const secondExpired = isPaymentMethodExpired(secondPaymentMethod);
				if (firstExpired !== secondExpired) return firstExpired ? -1 : 1;
				return (
					paymentMethodExpirationValue(firstPaymentMethod) -
					paymentMethodExpirationValue(secondPaymentMethod)
				);
			}
		);

		return (
			<section className={styles.billingManagementPanel}>
				<div className={styles.billingManagementHeader}>
					<div className={styles.billingManagementHeading}>
						<div className={styles.billingEyebrow}>Billing</div>
						<h3>{managementSection.title}</h3>
						<p>{managementSection.description}</p>
					</div>
					<PrimaryButton
						className={styles.billingHeaderButton}
						disabled={Boolean(paymentActionLoading) || Boolean(paymentSetup)}
						onClick={() => startPaymentMethodSetup()}>
						<Add />
						Add payment method
					</PrimaryButton>
				</div>

				{sectionLoading ? (
					renderPaymentMethodLoading()
				) : sectionError ? (
					<Alert className={styles.billingAlert} severity='error'>
						{sectionError}
					</Alert>
				) : paymentMethods.length ? (
					<>
						<div className={styles.billingPaymentMethodListHeader}>
							<span>Saved payment methods</span>
						</div>

						<div className={styles.billingPaymentMethodGrid}>
							{sortedPaymentMethods.map((paymentMethod) => {
								const isExpired = isPaymentMethodExpired(paymentMethod);
								const paymentMethodLabel =
									formatPaymentMethodLabel(paymentMethod);
								return (
									<div
										className={`${styles.billingPaymentMethodCard} ${
											paymentMethod.isDefault
												? styles.billingPaymentMethodCardDefault
												: ''
										} ${
											isExpired ? styles.billingPaymentMethodCardExpired : ''
										}`}
										key={paymentMethod.id}>
										<div className={styles.billingPaymentMethodCardTop}>
											<div className={styles.billingPaymentMethodTitle}>
												<span className={styles.billingPaymentMethodIcon}>
													<Payment />
												</span>
												<div>
													<strong>{paymentMethodLabel}</strong>
													<span>
														{formatPaymentMethodExpiration(paymentMethod)}
													</span>
												</div>
											</div>
											<div className={styles.billingPaymentMethodBadges}>
												{paymentMethod.isDefault ? <span>Default</span> : null}
												{isExpired ? (
													<span
														className={
															styles.billingPaymentMethodBadgeDanger
														}>
														Expired
													</span>
												) : null}
											</div>
										</div>
										<div className={styles.billingPaymentMethodActions}>
											{isExpired ? (
												<button
													aria-label={`Replace ${paymentMethodLabel}`}
													className={styles.billingTextButton}
													disabled={
														Boolean(paymentActionLoading) ||
														Boolean(paymentSetup)
													}
													onClick={() =>
														startPaymentMethodSetup({
															mode: 'replace',
															paymentMethod,
														})
													}
													type='button'>
													<Add />
													Replace payment method
												</button>
											) : null}
											{paymentMethod.isDefault || isExpired ? null : (
												<button
													aria-label={`Set ${paymentMethodLabel} as default`}
													className={styles.billingTextButton}
													disabled={
														Boolean(paymentActionLoading) ||
														Boolean(paymentSetup)
													}
													onClick={() =>
														setDefaultSavedPaymentMethod(paymentMethod.id)
													}
													type='button'>
													<StarBorder />
													{paymentActionLoading ===
													`default:${paymentMethod.id}`
														? 'Setting default'
														: 'Set default'}
												</button>
											)}
											<button
												aria-label={`Remove ${paymentMethodLabel}`}
												className={`${styles.billingTextButton} ${styles.billingDangerButton}`}
												disabled={
													paymentMethods.length <= 1 ||
													Boolean(paymentActionLoading) ||
													Boolean(paymentSetup)
												}
												onClick={() =>
													requestDeleteSavedPaymentMethod(paymentMethod)
												}
												title={
													paymentMethods.length <= 1
														? 'At least one payment method must stay on file.'
														: 'Remove payment method'
												}
												type='button'>
												<DeleteOutline />
												{paymentActionLoading === `delete:${paymentMethod.id}`
													? 'Removing'
													: 'Remove'}
											</button>
										</div>
										{paymentMethods.length <= 1 ? (
											<div className={styles.billingPaymentMethodLockNote}>
												At least one payment method must stay on file.
											</div>
										) : null}
									</div>
								);
							})}
						</div>
					</>
				) : (
					<div className={styles.billingPaymentMethodEmpty}>
						<span className={styles.billingPaymentMethodEmptyIcon}>
							<Payment />
						</span>
						<div>
							<strong>No saved payment methods yet.</strong>
							<span>
								Use Add payment method to save a default payment method for
								future invoices.
							</span>
						</div>
					</div>
				)}
			</section>
		);
	};

	const renderInvoices = () => (
		<section className={styles.billingManagementPanel}>
			<div className={styles.billingManagementHeader}>
				<div>
					<div className={styles.billingEyebrow}>Billing</div>
					<h3>{managementSection.title}</h3>
					<p>{managementSection.description}</p>
				</div>
			</div>

			{sectionLoading ? (
				renderInvoiceLoading()
			) : sectionError ? (
				<Alert className={styles.billingAlert} severity='error'>
					{sectionError}
				</Alert>
			) : invoices.length ? (
				<>
					<div className={styles.billingInvoiceOverview}>
						<div className={styles.billingInvoiceOverviewItem}>
							<span>Latest invoice</span>
							<strong>
								{formatInvoiceMoney(
									latestInvoice?.total,
									latestInvoice?.currency
								)}
							</strong>
							<small>{formatDate(latestInvoice?.created)}</small>
						</div>
						<div className={styles.billingInvoiceOverviewItem}>
							<span>Open balance</span>
							<strong>
								{invoiceOpenBalance ||
									formatInvoiceMoney(0, latestInvoice?.currency)}
							</strong>
							<small>
								{invoiceOpenCount
									? `${invoiceOpenCount} invoice${
											invoiceOpenCount === 1 ? '' : 's'
									  } with a balance`
									: 'No balance due'}
							</small>
						</div>
						<div className={styles.billingInvoiceOverviewItem}>
							<span>Records shown</span>
							<strong>{invoices.length}</strong>
							<small>Most recent invoices first</small>
						</div>
					</div>

					<div
						aria-label='Invoice history'
						className={styles.billingInvoiceTable}
						role='table'>
						<div
							className={styles.billingInvoiceTableHeader}
							role='row'>
							{invoiceTableHeaders.map((header) => (
								<span key={header} role='columnheader'>
									{header}
								</span>
							))}
						</div>
						<div className={styles.billingInvoiceList} role='rowgroup'>
							{invoices.map((invoice) => {
								const hasBalanceDue = invoiceHasBalanceDue(invoice);
								const documentUrl = invoiceDocumentUrl(invoice);
								return (
									<div
										className={styles.billingInvoiceRow}
										key={invoice.id}
										role='row'>
										<div
											className={styles.billingInvoicePrimary}
											role='cell'>
											<ReceiptLong className={styles.billingMetricIcon} />
											<div>
												<strong>{invoice.number || 'Invoice'}</strong>
												<span>{formatDate(invoice.created)}</span>
											</div>
										</div>
										<div
											className={styles.billingInvoicePeriod}
											role='cell'>
											<span className={styles.billingInvoiceMobileLabel}>
												Period
											</span>
											<strong>
												{`${formatDate(invoice.periodStart)} - ${formatDate(
													invoice.periodEnd
												)}`}
											</strong>
										</div>
										<div
											className={styles.billingInvoiceAmount}
											role='cell'>
											<span className={styles.billingInvoiceMobileLabel}>
												Total
											</span>
											<strong>
												{formatInvoiceMoney(invoice.total, invoice.currency)}
											</strong>
										</div>
										<div
											className={`${styles.billingInvoiceAmount} ${
												hasBalanceDue
													? styles.billingInvoiceBalanceDue
													: ''
											}`}
											role='cell'>
											<span className={styles.billingInvoiceMobileLabel}>
												Balance
											</span>
											<strong>
												{formatInvoiceMoney(
													invoice.amountRemaining,
													invoice.currency
												)}
											</strong>
											{hasBalanceDue && invoice.dueDate ? (
												<small>Due {formatDate(invoice.dueDate)}</small>
											) : null}
										</div>
										<div
											className={styles.billingInvoiceStatusCell}
											role='cell'>
											<span
												className={`${styles.billingStatusPill} ${
													invoice.status === 'paid' || invoice.paid
														? styles.billingStatusPillPaid
														: ''
												} ${
													hasBalanceDue
														? styles.billingStatusPillDue
														: ''
												}`}>
												{invoiceStatusLabel(invoice.status)}
											</span>
										</div>
										<div
											className={styles.billingInvoiceActionCell}
											role='cell'>
											{documentUrl ? (
												<a
													className={styles.billingInvoiceLink}
													href={documentUrl}
													rel='noreferrer'
													target='_blank'>
													{invoice.invoicePdf ? 'Open PDF' : 'View'}
												</a>
											) : (
												<span className={styles.billingInvoiceUnavailable}>
													Not ready
												</span>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</>
			) : (
				<div className={styles.billingInlineEmpty}>
					<ReceiptLong />
					<span>No invoices yet.</span>
				</div>
			)}
		</section>
	);

	const billingHeader = isMd ? (
		<div className={styles.billingMobileHeader}>
			<DashboardToggle className={styles.billingMobileDashboardToggle} />
			<span>Billing</span>
		</div>
	) : null;
	const subscriptionProcessing = actionLoading
		? subscriptionProcessingCopy()
		: null;

	return (
		<PageLayout title='Billing' header={billingHeader}>
			<div className={styles.billingDashboard}>
				{loading ? (
					billingSection === 'plans' ? (
						renderPlansLoading()
					) : (
						<div className={styles.billingLoading}>
							<div className={styles.billingLoadingBar} />
							<div className={styles.billingLoadingGrid}>
								<div />
								<div />
								<div />
							</div>
						</div>
					)
				) : (
					<>
						{error ? (
							<Alert className={styles.billingAlert} severity='error'>
								{error}
							</Alert>
						) : null}
						{notice ? (
							<Alert className={styles.billingAlert} severity='success'>
								{notice}
							</Alert>
						) : null}
						{billing?.pendingChange ? (
							<Alert className={styles.billingAlert} severity='info'>
								{`Scheduled change to ${billing.pendingChange.planName} with ${billing.pendingChange.purchasedSeatQuantity} paid seats.`}
							</Alert>
						) : null}

						{billingSection === 'plans' ? (
							<>
								<section className={styles.billingSummaryBand}>
									<div className={styles.billingHeroTop}>
										<div>
											<div className={styles.billingEyebrow}>Current plan</div>
											<h2>{billing?.planName || 'Free'}</h2>
											<div className={styles.billingStatusLine}>
												<span className={styles.billingStatusDot} />
												{statusLabels[billing?.status] ||
													billing?.status ||
													'Free'}
											</div>
										</div>
										<div className={styles.billingSummaryMeta}>
											<div>
												<span>Monthly cost</span>
												<strong>{formatMoney(currentMonthlyCents)}</strong>
											</div>
											<div>
												<span>Next renewal</span>
												<strong>{formatDate(billing?.currentPeriodEnd)}</strong>
											</div>
										</div>
									</div>
								</section>

								<section
									className={styles.billingMetricGrid}
									style={metricGridStyle}>
									{metricItems.map(
										({ label, value, detail, icon: MetricIcon }) => (
											<div className={styles.billingMetric} key={label}>
												<span className={styles.billingMetricIconWrap}>
													<MetricIcon className={styles.billingMetricIcon} />
												</span>
												<span className={styles.billingMetricLabel}>
													{label}
												</span>
												<strong>{value}</strong>
												{detail ? <small>{detail}</small> : null}
											</div>
										)
									)}
								</section>

								<section className={styles.billingSectionHeader}>
									<div>
										<h3>
											{editingSubscription
												? 'Change subscription'
												: 'Plans & seats'}
										</h3>
										<p>
											{editingSubscription
												? `Choose a plan and paid-seat quantity.${
														giftedSeatQuantity > 0
															? ' Gifted seats are included automatically.'
															: ''
												  }`
												: 'Your current plan and seat usage are shown above.'}
										</p>
									</div>
									{editingSubscription ? (
										<button
											className={styles.billingTextButton}
											disabled={actionLoading}
											onClick={resetBillingDraft}
											type='button'>
											Cancel changes
										</button>
									) : (
										<PrimaryButton
											className={styles.billingHeaderButton}
											disabled={!plans.length || actionLoading}
											onClick={startSubscriptionEdit}>
											{subscriptionActionLabel}
										</PrimaryButton>
									)}
								</section>

								{editingSubscription ? (
									<>
										<section className={styles.billingPlanGrid}>
											{plans.map((plan) => {
												const selected = plan.planCode === selectedPlanCode;
												const current = plan.planCode === billing?.planCode;
												const requiredSeats = requiredPaidSeatsForPlan(plan);
												const planEstimate = planEstimateForDraft(plan);
												const savingsPercent = savingsPercentForPlan(plan);
												const direction = changeDirectionLabel(plan);
												const hasBadges = Boolean(
													current || direction || savingsPercent
												);
												return (
													<button
														type='button'
														key={plan.planCode}
														className={`${styles.billingPlanCard} ${
															selected ? styles.billingPlanCardSelected : ''
														}`}
														onClick={() => selectPlan(plan)}>
														{hasBadges ? (
															<div className={styles.billingPlanBadges}>
																{current ? (
																	<span
																		className={`${styles.billingPlanBadge} ${styles.billingPlanBadgeState}`}>
																		Current
																	</span>
																) : null}
																{direction ? (
																	<span
																		className={`${styles.billingPlanBadge} ${
																			styles.billingPlanBadgeDirection
																		} ${
																			direction === 'Downgrade'
																				? styles.billingPlanBadgeDowngrade
																				: ''
																		}`}>
																		{direction}
																	</span>
																) : null}
																{savingsPercent ? (
																	<span
																		className={`${styles.billingPlanBadge} ${styles.billingSavingsBadge}`}>
																		{`Save ${savingsPercent}%`}
																	</span>
																) : null}
															</div>
														) : null}
														<h4 className={styles.billingPlanName}>
															{plan.name}
														</h4>
														<div className={styles.billingPlanPriceBlock}>
															<strong>
																{formatMoney(planEstimate.amountCents)}
															</strong>
															<span>/ month</span>
														</div>
														<div className={styles.billingPlanContextLine}>
															{planEstimateContext(plan, planEstimate.seats)}
														</div>
														<p className={styles.billingPlanDescription}>
															{planPricingDetail(plan)}
														</p>
														{Number(paidSeats || 0) < requiredSeats ? (
															<div className={styles.billingPlanMinimumLine}>
																{`Selecting this plan will raise the quantity to ${requiredSeats}.`}
															</div>
														) : null}
													</button>
												);
											})}
										</section>

										<section className={styles.billingReviewPanel}>
											<div className={styles.billingQuantityBlock}>
												<div>
													<div className={styles.billingEyebrow}>
														Plan size
													</div>
													<h3>{selectedPlan?.name || 'Select a plan'}</h3>
													<p>
														{selectedPlan
															? `This selection requires at least ${selectedMinimumPaidSeats} paid seats.`
															: 'Choose a plan to set paid seats.'}
													</p>
													{selectedPlanUsesRaisedMinimum ? (
														<div className={styles.billingSeatMinimumNote}>
															{`Minimum is ${selectedMinimumPaidSeats} paid seats to cover your current users.`}
														</div>
													) : null}
												</div>
												<div className={styles.billingQuantityControls}>
													<div className={styles.billingStepper}>
														<IconButton
															aria-label='Remove paid seat'
															disabled={
																!selectedPlan ||
																actionLoading ||
																paidSeats <= selectedMinimumPaidSeats
															}
															onClick={() => updatePaidSeats(paidSeats - 1)}>
															<Remove />
														</IconButton>
														<input
															aria-label='Paid seats'
															type='number'
															min={selectedMinimumPaidSeats}
															value={paidSeats}
															disabled={!selectedPlan || actionLoading}
															onChange={(event) =>
																updatePaidSeats(event.target.value)
															}
														/>
														<IconButton
															aria-label='Add paid seat'
															disabled={!selectedPlan || actionLoading}
															onClick={() => updatePaidSeats(paidSeats + 1)}>
															<Add />
														</IconButton>
													</div>
												</div>
											</div>

											<div className={styles.billingReviewGrid}>
												<div>
													<span>Paid seats</span>
													<strong>{paidSeats}</strong>
												</div>
												<div>
													<span>Total seats after change</span>
													<strong>{previewCapacity}</strong>
												</div>
												<div>
													<span>Monthly cost</span>
													<strong>{formatMoney(estimatedMonthlyCents)}</strong>
												</div>
											</div>

											<div className={styles.billingActionRow}>
												<div className={styles.billingActionNote}>
													<Autorenew />
													<span>
														{hasSubscription
															? 'Seat increases apply now. Decreases take effect at your next renewal.'
															: 'You will review payment before the plan starts.'}
													</span>
												</div>
												<div className={styles.billingDraftActions}>
													<button
														className={styles.billingTextButton}
														disabled={actionLoading}
														onClick={resetBillingDraft}
														type='button'>
														Cancel changes
													</button>
													<PrimaryButton
														disabled={
															actionLoading ||
															!selectedPlan ||
															isCurrentSelection
														}
														onClick={openSubscriptionChangeConfirmation}>
														{primaryActionLabel()}
													</PrimaryButton>
												</div>
											</div>
										</section>
									</>
								) : null}
							</>
						) : (
							<>
								{billingSection === 'paymentMethods'
									? renderPaymentMethods()
									: renderInvoices()}
							</>
						)}
					</>
				)}

				{billingSection === 'plans' && !loading && !plans.length ? (
					<div className={styles.billingEmptyState}>
						<ErrorOutline />
						<span>No billing plans are available.</span>
					</div>
				) : null}
			</div>

			<Dialog
				aria-labelledby='billing-subscription-change-dialog-title'
				fullWidth
				maxWidth='sm'
				onClose={closeSubscriptionChangeDialog}
				open={Boolean(pendingSubscriptionChange)}
				PaperProps={{
					className: styles.billingConfirmDialogPaper,
				}}>
				<DialogTitle
					className={styles.billingConfirmDialogTitle}
					id='billing-subscription-change-dialog-title'>
					<div>
						<span>{pendingSubscriptionChange?.title}</span>
						<p>{pendingSubscriptionChange?.timingLabel}</p>
					</div>
					<IconButton
						aria-label='Close billing change confirmation'
						disabled={actionLoading}
						onClick={closeSubscriptionChangeDialog}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.billingConfirmDialogContent}>
					<p className={styles.billingConfirmDialogMessage}>
						{pendingSubscriptionChange?.description}
					</p>
					{subscriptionActionError ? (
						<Alert
							className={styles.billingConfirmDialogAlert}
							severity='error'>
							{subscriptionActionError}
						</Alert>
					) : null}
					<div className={styles.billingChangeConfirmSummary}>
						<div className={styles.billingChangeConfirmCard}>
							<span>Current</span>
							<strong>{pendingSubscriptionChange?.current}</strong>
							<small>
								{pendingSubscriptionChange?.currentMonthlyCost} per month
							</small>
						</div>
						<div
							aria-hidden='true'
							className={styles.billingChangeConfirmArrow}>
							<ArrowForward />
						</div>
						<div className={styles.billingChangeConfirmCard}>
							<span>New</span>
							<strong>{pendingSubscriptionChange?.target}</strong>
							<small>
								{pendingSubscriptionChange?.targetMonthlyCost} per month
							</small>
						</div>
					</div>
					{pendingSubscriptionChange?.proration ? (
						<div
							aria-live='polite'
							className={`${styles.billingChangeProrationLine} ${
								pendingSubscriptionChange.proration.unavailable
									? styles.billingChangeProrationLineMuted
									: ''
							}`}>
							<div>
								<span>{pendingSubscriptionChange.proration.label}</span>
								<p>{pendingSubscriptionChange.proration.detail}</p>
							</div>
							<strong>{pendingSubscriptionChange.proration.amount}</strong>
						</div>
					) : null}
					{pendingSubscriptionChange?.notes?.length ? (
						<div className={styles.billingChangeConfirmNotes}>
							{pendingSubscriptionChange.notes.map((note) => (
								<p key={note}>{note}</p>
							))}
						</div>
					) : null}
					{subscriptionProcessing ? (
						<div
							aria-live='assertive'
							className={styles.billingChangeProcessing}>
							<CircularProgress size={22} thickness={4.5} />
							<div>
								<strong>{subscriptionProcessing.title}</strong>
								<p>{subscriptionProcessing.detail}</p>
							</div>
						</div>
					) : null}
					<div className={styles.billingConfirmDialogFooter}>
						<button
							className={styles.billingTextButton}
							disabled={actionLoading}
							onClick={closeSubscriptionChangeDialog}
							type='button'>
							Cancel
						</button>
						<PrimaryButton
							disabled={
								actionLoading ||
								Boolean(pendingSubscriptionChange?.proration?.loading)
							}
							onClick={handlePrimaryAction}>
							{actionLoading
								? 'Saving...'
								: pendingSubscriptionChange?.confirmLabel}
						</PrimaryButton>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog
				aria-labelledby='billing-subscription-checkout-dialog-title'
				fullScreen={isMd}
				fullWidth
				maxWidth='sm'
				onClose={closeSubscriptionCheckout}
				open={Boolean(subscriptionCheckout)}
				PaperProps={{
					className: styles.billingPaymentDialogPaper,
				}}>
				<DialogTitle
					className={styles.billingPaymentDialogTitle}
					id='billing-subscription-checkout-dialog-title'>
					<div>
						<span>{subscriptionCheckoutTitle}</span>
						<p>{subscriptionCheckoutDescription}</p>
					</div>
					<IconButton
						aria-label='Close payment checkout'
						disabled={subscriptionCheckoutCompleting}
						onClick={closeSubscriptionCheckout}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.billingPaymentDialogContent}>
					{subscriptionCheckout?.clientSecret && subscriptionStripePromise ? (
						<EmbeddedBillingCheckout
							key={subscriptionCheckout.checkoutSessionId}
							clientSecret={subscriptionCheckout.clientSecret}
							stripePromise={subscriptionStripePromise}
							onComplete={completeSubscriptionCheckout}
						/>
					) : (
						<div className={styles.billingInlineLoading}>
							Loading payment checkout...
						</div>
					)}
					<div className={styles.billingPaymentDialogFooter}>
						<button
							className={styles.billingTextButton}
							disabled={subscriptionCheckoutCompleting}
							onClick={closeSubscriptionCheckout}
							type='button'>
							Cancel
						</button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog
				aria-labelledby='billing-payment-method-dialog-title'
				fullScreen={isMd}
				fullWidth
				maxWidth='sm'
				onClose={closePaymentMethodSetup}
				open={Boolean(paymentSetup)}
				PaperProps={{
					className: styles.billingPaymentDialogPaper,
				}}>
				<DialogTitle
					className={styles.billingPaymentDialogTitle}
					id='billing-payment-method-dialog-title'>
					<div>
						<span>{paymentSetupTitle}</span>
						<p>{paymentSetupDescription}</p>
					</div>
					<IconButton
						aria-label={`Close ${paymentSetupTitle.toLowerCase()}`}
						disabled={paymentActionLoading === 'confirm'}
						onClick={closePaymentMethodSetup}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.billingPaymentDialogContent}>
					{paymentSetup?.clientSecret && paymentStripePromise ? (
						<EmbeddedBillingCheckout
							key={paymentSetup.checkoutSessionId}
							clientSecret={paymentSetup.clientSecret}
							stripePromise={paymentStripePromise}
							onComplete={completePaymentMethodSetup}
						/>
					) : (
						<div className={styles.billingInlineLoading}>
							Loading payment setup...
						</div>
					)}
					<div className={styles.billingPaymentDialogFooter}>
						<button
							className={styles.billingTextButton}
							disabled={paymentActionLoading === 'confirm'}
							onClick={closePaymentMethodSetup}
							type='button'>
							Cancel
						</button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog
				aria-labelledby='billing-remove-payment-method-dialog-title'
				fullWidth
				maxWidth='xs'
				onClose={closeRemovePaymentMethodDialog}
				open={Boolean(paymentMethodToDelete)}
				PaperProps={{
					className: styles.billingConfirmDialogPaper,
				}}>
				<DialogTitle
					className={styles.billingConfirmDialogTitle}
					id='billing-remove-payment-method-dialog-title'>
					<div>
						<span>Remove payment method?</span>
						<p>{formatPaymentMethodLabel(paymentMethodToDelete)}</p>
					</div>
					<IconButton
						aria-label='Close remove payment method confirmation'
						disabled={deletingPaymentMethod}
						onClick={closeRemovePaymentMethodDialog}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.billingConfirmDialogContent}>
					<p className={styles.billingConfirmDialogMessage}>
						This payment method will no longer be used for future agency
						billing. At least one payment method must stay on file.
					</p>
					<div className={styles.billingConfirmDialogFooter}>
						<button
							className={styles.billingTextButton}
							disabled={deletingPaymentMethod}
							onClick={closeRemovePaymentMethodDialog}
							type='button'>
							Cancel
						</button>
						<button
							className={`${styles.billingTextButton} ${styles.billingDangerButton}`}
							disabled={deletingPaymentMethod}
							onClick={deleteSavedPaymentMethod}
							type='button'>
							<DeleteOutline />
							{deletingPaymentMethod ? 'Removing...' : 'Remove'}
						</button>
					</div>
				</DialogContent>
			</Dialog>
		</PageLayout>
	);
};

export default Billing;
